<template>
    <div class="stripe-input-wrapper">
        <LoadingAnimation
            v-if="isStatusLoading"
            class="w-100 d-flex align-items-center"
            style="height: 55px"
            animation-style="bars"
        ></LoadingAnimation>

        <form id="payment-form" style="width: 100%" class="d-flex flex-column gap-10">
            <div id="payment-element">
                <!-- Elements will create form elements here -->
            </div>
            <div v-if="cardError" class="stripe-error-container text-danger mt-1">{{ cardError }}</div>
            <div class="d-flex align-items-center gap-10 text-muted font-size-75">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M9 3.64252e-09C10.3971 -5.12794e-05 11.7399 0.541409 12.7462 1.51062C13.7525 2.47983 14.344 3.80134 14.3964 5.1975L14.4 5.4H15.3C15.7541 5.39986 16.1915 5.57137 16.5245 5.88015C16.8575 6.18893 17.0614 6.61216 17.0955 7.065L17.1 7.2V16.2C17.1001 16.6541 16.9286 17.0915 16.6199 17.4245C16.3111 17.7575 15.8878 17.9614 15.435 17.9955L15.3 18H2.7C2.24588 18.0001 1.80849 17.8286 1.47551 17.5199C1.14253 17.2111 0.938561 16.7878 0.904502 16.335L0.900002 16.2V7.2C0.899858 6.74588 1.07137 6.30849 1.38015 5.97551C1.68893 5.64252 2.11216 5.43856 2.565 5.4045L2.7 5.4H3.6C3.6 3.96783 4.16893 2.59432 5.18163 1.58162C6.19432 0.568927 7.56783 3.64252e-09 9 3.64252e-09ZM15.3 7.2H2.7V16.2H15.3V7.2ZM9 9C9.38393 9.00012 9.75777 9.123 10.0669 9.35068C10.376 9.57837 10.6043 9.89893 10.7183 10.2655C10.8323 10.6322 10.826 11.0256 10.7005 11.3885C10.575 11.7513 10.3368 12.0645 10.0206 12.2823L9.9 12.3588V13.5C9.89975 13.7294 9.81191 13.95 9.65444 14.1168C9.49697 14.2836 9.28174 14.384 9.05275 14.3975C8.82375 14.4109 8.59826 14.3364 8.42235 14.1892C8.24644 14.0419 8.1334 13.8331 8.1063 13.6053L8.1 13.5V12.3588C7.75687 12.1607 7.4887 11.8549 7.33708 11.4888C7.18546 11.1227 7.15886 10.7168 7.26142 10.3341C7.36397 9.95139 7.58994 9.6132 7.90428 9.37199C8.21862 9.13077 8.60378 9.00002 9 9ZM9 1.8C8.04522 1.8 7.12955 2.17928 6.45442 2.85442C5.77929 3.52955 5.4 4.44522 5.4 5.4H12.6C12.6 4.44522 12.2207 3.52955 11.5456 2.85442C10.8705 2.17928 9.95478 1.8 9 1.8Z" fill="#6C757D"/>
                </svg>
                {{ 'All transactions are SSL encrypted.' | trans }}
            </div>
        </form>
    </div>
</template>

<script>
import { pageNotification } from '../../../../../qaamgo/helper/notify';
import { globalLogger } from '../../../../../qaamgo/helper/global-logger';
import DataStatus from '../../../../../qaamgo/modules/sat/components/mixins/DataStatus';
import LoadingAnimation from '../../../../../qaamgo/modules/sat/components/LoadingAnimation';
import Tooltip from '../../../../../qaamgo/modules/sat/components/form/Tooltip';
import TransFilter from '../../../../../qaamgo/modules/sat/components/mixins/TransFilter';
import { stripeHelper } from '../../../../payment/stripe-helper';
import { testIsNonEmptyString } from '../../../../../lib/test-and-assert/test-base';
import { satGlobals } from '../../../../../qaamgo/sat/helper/sat-globals';
import { translatePath } from '../../../../../qaamgo/sat/helper/translate-path';

let stripeApiLoadingPromise = null;

export default {
    name: 'CreditCardStripe',
    components: { Tooltip, LoadingAnimation },
    mixins: [DataStatus, TransFilter],
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        stripePaymentIntent: {
            type: String,
            default: '',
        },
    },
    beforeMount() {
        stripeApiLoadingPromise = new Promise((resolve, reject) => {
            // delay stripe form rendering to avoid performance issues on initial render
            setTimeout(() => {
                stripeHelper.initializeStripe().then(resolve).catch(reject);
            }, 500);
        });
    },
    mounted() {
        // only unblock once stripe api is loaded and initialized
        stripeApiLoadingPromise
            .then(() => {
                const clientSecret = this.stripePaymentIntent;
                // Fully customizable with appearance API.
                const appearance = {
                    variables: {
                        colorText: '#CBCBCB',
                        fontFamily: 'Open Sans,Helvetica,Arial,sans-serif',
                        borderRadius: '10px',
                    },
                };

                const options = {
                    layout: {
                        type: 'accordion',
                        defaultCollapsed: false,
                        radios: true,
                        spacedAccordionItems: true
                    },
                };

                this.stripe = stripeHelper.getStripe();
                this.stripeElements = this.stripe.elements({ clientSecret, appearance });

                const paymentElement = this.stripeElements.create('payment', options);
                paymentElement.mount('#payment-element');

                this.setStatusDone();
            })
            .catch((e) => {
                pageNotification.showGenericErrorMessage();
                globalLogger.log('stripe vue - error loading stripe api', e);
            });
    },
    methods: {
        hasErrors() {
            return testIsNonEmptyString(this.cardError);
        },
        setErrorMessage(message) {
            this.cardError = message;
        },
        confirmStripePayment(isSubscription) {
            // make sure users don't have to confirm that they want to be redirected
            window.allowLeave = true;

            let returnUrl = satGlobals.getAccountUrl() + '/register/stripe/order/finish';

            if (isSubscription) {
              returnUrl += '?isSubscription=true'
            }

            return this.stripe
                .confirmPayment({
                    elements: this.stripeElements,
                    confirmParams: {
                        return_url: returnUrl,
                    },
                })
                .then(function (result) {
                    // This point will only be reached if there is an immediate error when
                    // confirming the payment. Otherwise, your customer will be redirected to
                    // your `return_url`. For some payment methods like iDEAL, your customer will
                    // be redirected to an intermediate site first to authorize the payment, then
                    // redirected to the `return_url`.

                    if (result.error) {
                        return Promise.reject(result.error);
                    }

                    return result;
                });
        },
    },
    data() {
        return {
            stripe: null,
            stripeElements: null,
            cardError: '',
        };
    },
};
</script>

<style lang="scss">
@import '../../../../../../../../../../../node_modules/sass-material-colors/sass/sass-material-colors';

@import '../../../../../../scss/abstracts/mixins';
@import '../../../../../../scss/abstracts/functions';
@import '../../../../../../scss/abstracts/variables';
@import '../../../../../../../../../../../assets/scss/abstracts/variables';

@import '../../../../../../../../../../../node_modules/bootstrap/scss/functions';
@import '../../../../../../../../../../../node_modules/bootstrap/scss/variables';
@import '../../../../../../../../../../../node_modules/bootstrap/scss/mixins';

.StripeElement--invalid,
.StripeElement.invalid {
    border-color: $danger !important;
}

.StripeElement {
    padding: 1rem;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    background: white;
}
</style>
